import { type FC, useEffect, useState, useRef } from "react";
import AddClientModal from "../components/AddClientModal";
import { ApiResponse, Client, Page } from "../types";
import api from "../services/api";
import { formatDateTime } from "../utils/utils";

// --- Icons ---
const Icons = {
    User: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
    Mail: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
    Calendar: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    Qr: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4h2v-4zM6 8V4h6v4H6zm14 10a2 2 0 11-4 0 2 2 0 014 0zM6 20h6v-4H6v4z" /></svg>,
    Trash: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
    Plus: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
};

const ClientsPage: FC = () => {
    const [clients, setClients] = useState<Client[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Stati per gestione errori/successo e modale cancellazione
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
    const successMessageTimeoutRef = useRef<number | null>(null);

    const fetchClients = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.get<ApiResponse<Page<Client>>>(`/api/clients/getAll`, {
                params: { page, size: 10 }
            });
            const pageData = response.data.data;
            setClients(pageData.content);
            setTotalPages(pageData.totalPages);
        } catch (error) {
            console.error("Errore nel caricamento dei clienti:", error);
            setError("Impossibile caricare la lista dei clienti.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchClients();
    }, [page]);

    // Gestione timer messaggi
    useEffect(() => {
        if (successMessage) {
            if (successMessageTimeoutRef.current) clearTimeout(successMessageTimeoutRef.current);
            successMessageTimeoutRef.current = window.setTimeout(() => setSuccessMessage(null), 3000);
        }
        return () => {
            if (successMessageTimeoutRef.current) clearTimeout(successMessageTimeoutRef.current);
        };
    }, [successMessage]);

    const initiateDelete = (client: Client) => {
        setClientToDelete(client);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!clientToDelete) return;

        try {
            await api.delete(`/api/clients/delete/${clientToDelete.id}`);
            setShowDeleteModal(false);
            setClientToDelete(null);
            setSuccessMessage("Cliente eliminato con successo.");
            fetchClients();
        } catch (error) {
            console.error("Errore durante l'eliminazione del cliente:", error);
            setError("Impossibile eliminare il cliente. Riprova.");
            setShowDeleteModal(false);
        }
    };

    const handleClientAdded = () => {
        setSuccessMessage("Cliente aggiunto con successo.");
        fetchClients();
    };

    return (
        <div className="space-y-8 animate-fade-in">
            <AddClientModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onClientAdded={handleClientAdded} />

            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div>
                    <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                        Gestione Clienti
                    </h1>
                    <p className="text-slate-400 mt-2 text-lg">
                        Anagrafica clienti e associazioni QR Code
                    </p>
                </div>

                <button
                    onClick={() => setIsModalOpen(true)}
                    className="px-6 py-2.5 rounded-xl text-white font-bold bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 shadow-lg shadow-sky-500/20 transition-all transform active:scale-95 flex items-center space-x-2"
                >
                    {Icons.Plus}
                    <span>Aggiungi Cliente</span>
                </button>
            </div>

            {/* Notifications */}
            {error && (
                <div className="p-4 bg-red-900/20 border border-red-800/50 backdrop-blur text-red-200 rounded-xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span>{error}</span>
                </div>
            )}
            {successMessage && (
                <div className="p-4 bg-emerald-900/20 border border-emerald-800/50 backdrop-blur text-emerald-200 rounded-xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                    <span>{successMessage}</span>
                </div>
            )}

            {/* Main Content Card */}
            <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl shadow-xl overflow-hidden">
                <div className="overflow-x-auto min-h-[300px]">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-900/60 text-xs uppercase text-slate-400 font-semibold tracking-wider">
                        <tr>
                            <th className="p-5 border-b border-slate-700/50">Nome Cliente</th>
                            <th className="p-5 border-b border-slate-700/50 hidden sm:table-cell">Email Contatto</th>
                            <th className="p-5 border-b border-slate-700/50 hidden md:table-cell">Data Creazione</th>
                            <th className="p-5 border-b border-slate-700/50 text-center">QR Associati</th>
                            <th className="p-5 border-b border-slate-700/50 text-right">Azioni</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                        {isLoading ? (
                            <tr>
                                <td colSpan={5} className="p-12 text-center text-slate-400">
                                    <div className="flex justify-center items-center gap-3">
                                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-sky-500"></div>
                                        Caricamento...
                                    </div>
                                </td>
                            </tr>
                        ) : clients.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="p-12 text-center text-slate-500">
                                    <div className="flex flex-col items-center justify-center gap-3">
                                        <svg className="w-12 h-12 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                                        <p>Nessun cliente trovato.</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            clients.map(client => (
                                <tr key={client.id} className="hover:bg-slate-700/30 transition-colors group">
                                    <td className="p-5">
                                        <div className="font-semibold text-white flex items-center gap-3">
                                            <div className="p-2 bg-slate-700/50 rounded-lg text-slate-400 group-hover:text-sky-400 group-hover:bg-sky-400/10 transition-colors">
                                                {Icons.User}
                                            </div>
                                            {client.name}
                                        </div>
                                    </td>
                                    <td className="p-5 hidden sm:table-cell">
                                        <div className="flex items-center gap-2 text-slate-300">
                                            <span className="text-slate-500">{Icons.Mail}</span>
                                            {client.email}
                                        </div>
                                    </td>
                                    <td className="p-5 hidden md:table-cell">
                                        <div className="flex items-center gap-2 text-slate-300">
                                            <span className="text-slate-500">{Icons.Calendar}</span>
                                            {formatDateTime(client.created)}
                                        </div>
                                    </td>
                                    <td className="p-5 text-center">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20">
                                                {client.qrCount}
                                            </span>
                                    </td>
                                    <td className="p-5 text-right">
                                        <button
                                            onClick={() => initiateDelete(client)}
                                            className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                                            title="Elimina Cliente"
                                        >
                                            {Icons.Trash}
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="p-4 border-t border-slate-700/50 bg-slate-800/30 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-400">
                    <span>Pagina <span className="text-white font-semibold">{page + 1}</span> di {totalPages === 0 ? 1 : totalPages}</span>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setPage(p => p - 1)}
                            disabled={page === 0 || isLoading}
                            className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-xs uppercase tracking-wide"
                        >
                            Precedente
                        </button>
                        <button
                            onClick={() => setPage(p => p + 1)}
                            disabled={page >= totalPages - 1 || isLoading}
                            className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-xs uppercase tracking-wide"
                        >
                            Successiva
                        </button>
                    </div>
                </div>
            </div>

            {/* Custom Delete Confirmation Modal */}
            {showDeleteModal && clientToDelete && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-fade-in">
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl transform transition-all scale-100">
                        <div className="p-6">
                            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4 text-red-500">
                                {Icons.Trash}
                            </div>
                            <h3 className="text-xl font-bold text-white">Conferma Eliminazione</h3>
                            <p className="text-slate-400 mt-2">
                                Sei sicuro di voler eliminare il cliente <span className="text-white font-semibold">"{clientToDelete.name}"</span>?
                                <br />Verranno rimossi anche tutti gli utenti associati.
                            </p>
                        </div>
                        <div className="p-4 bg-slate-900/50 rounded-b-2xl flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-5 py-2.5 rounded-xl text-slate-300 font-medium hover:bg-slate-700 transition-colors"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-5 py-2.5 rounded-xl text-white font-semibold bg-red-600 hover:bg-red-500 shadow-lg shadow-red-500/20 transition-all"
                            >
                                Elimina
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientsPage;